import React, { useState } from "react";
import "@styles/pages/Login/Password/ResetPassword.scss";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ResetPasswordSchema } from "@models/ValidationSchema";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Trans, useTranslation } from "react-i18next";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { resetPassword } from "@api/authenticationApi";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { cryptoPassword } from "@utils/cryptoPassword";
const ResetPassword = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(ResetPasswordSchema),
    mode: "onChange",
  });
  const [searchParams] = useSearchParams();
  const reset_code = searchParams.get("reset_code") || "";
  const sua_id = searchParams.get("sua_id") || "";

  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (data) => {
    const finalData = {
      sua_id,
      reset_code,
      password: cryptoPassword(data?.password),
    };
    setIsLoading(true);
        
    try {
      const res = await resetPassword(finalData);
      const statusCode = res.data.StatusCode;
      switch (statusCode) {
        case 200:
          toast.success(t("status_message:status_code_200_reset_password"));
          navigate("/login");
          break;
        case 306:
          toast.error(t("status_message:status_code_306"));
          break;
        case 307:
          toast.error(t("status_message:status_code_307"));
          break;
        case 500:
          toast.error(t("status_message:status_code_500"));
          break;
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
    resetPassword();
  };

  return (
    <Box className="reset-password-container">
      <Box className="reset-password-detail-container">
        <Typography className="title">
          <Trans i18nKey="login:reset_password" />
        </Typography>
        <Typography className="description">
          <Trans i18nKey="login:password_rules" />
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Password */}
          <Box className="field-container">
            <Typography>
              <Trans i18nKey="login:password" />
            </Typography>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type={showPwd ? "text" : "password"}
                  error={!!errors.password}
                  helperText={
                    errors.password?.message ? t("login:password_rules") : ""
                  }
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPwd((v) => !v)}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {showPwd ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Confirm Password */}
          <Box className="field-container">
            <Typography>
              <Trans i18nKey="login:confirm_password" />
            </Typography>
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type={showConfirmPwd ? "text" : "password"}
                  error={!!errors.confirmPassword}
                  helperText={
                    errors.confirmPassword?.message
                      ? t("login:confirm_password_rules")
                      : " "
                  }
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPwd((v) => !v)}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {showConfirmPwd ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <LoadingButton
            type="submit"
            className="loading-button"
            disabled={isLoading}
            loading={isLoading}
            fullWidth
          >
            <Trans i18nKey="login:reset_password" />
          </LoadingButton>
        </form>
      </Box>
    </Box>
  );
};

export default ResetPassword;
